/**
 * useLanguage Hook
 * 
 * Manages language state and provides translation utilities.
 * Now integrates with react-i18next for centralized translations.
 * 
 * @module hooks/useLanguage
 * 
 * Language Strategy:
 * - Priority: localStorage > browser locale > English fallback
 * - Auto-detect: Uses browser locale (navigator.language)
 * - Fallback: English ('en') for non-Korean locales (international default)
 * - Supported: 'en' (English), 'ko' (Korean), 'zh-CN' (Chinese), 'pt-BR' (Portuguese), 'es' (Spanish),
 *   'de' (German), 'fr' (French), 'it' (Italian), 'nl' (Dutch), 'sv' (Swedish), 'pl' (Polish)
 * - Persists: Saves user choice to localStorage
 * 
 * Usage:
 * ```ts
 * const { language, setLanguage, t, ti18n } = useLanguage();
 * 
 * // Legacy: inline translation helper (deprecated - use ti18n instead)
 * <span>{t('안녕하세요', 'Hello')}</span>
 * 
 * // New: namespace-based translations
 * <span>{ti18n('common:buttons.submit')}</span>
 * 
 * // Check current language
 * const greeting = language === 'ko' ? '환영합니다' : 'Welcome';
 * ```
 */
import { useState, useCallback, useEffect, Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { changeLanguage as i18nChangeLanguage, type SupportedLanguage } from '../i18n';
import type { LanguageCode } from '../types';

const LANGUAGE_STORAGE_KEY = 'heseda-ai-language';

/**
 * Detects browser language and returns appropriate LanguageCode
 * Maps browser locale to best matching supported language.
 */
function detectBrowserLanguage(): LanguageCode {
  try {
    const browserLang = navigator.language || navigator.languages?.[0] || '';
    const normalizedLang = browserLang.trim().toLowerCase();
    if (normalizedLang === 'ko' || normalizedLang.startsWith('ko-')) return 'ko';
    if (normalizedLang === 'zh-cn' || normalizedLang === 'zh' || normalizedLang.startsWith('zh-')) return 'zh-CN';
    if (normalizedLang === 'pt-br' || normalizedLang.startsWith('pt-br')) return 'pt-BR';
    if (normalizedLang.startsWith('pt')) return 'pt-BR'; // pt → pt-BR
    if (normalizedLang === 'es' || normalizedLang.startsWith('es-')) return 'es';
    if (normalizedLang === 'de' || normalizedLang.startsWith('de-')) return 'de';
    if (normalizedLang === 'fr' || normalizedLang.startsWith('fr-')) return 'fr';
    if (normalizedLang === 'it' || normalizedLang.startsWith('it-')) return 'it';
    if (normalizedLang === 'nl' || normalizedLang.startsWith('nl-')) return 'nl';
    if (normalizedLang === 'sv' || normalizedLang.startsWith('sv-')) return 'sv';
    if (normalizedLang === 'pl' || normalizedLang.startsWith('pl-')) return 'pl';
    return 'en';
  } catch {
    // SSR or navigator not available - default to English
    return 'en';
  }
}

/**
 * Get initial language: localStorage > browser detection
 */
const VALID_LANGUAGES: readonly LanguageCode[] = ['en', 'ko', 'zh-CN', 'pt-BR', 'es', 'de', 'fr', 'it', 'nl', 'sv', 'pl'];

function getInitialLanguage(): LanguageCode {
  try {
    const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (stored && VALID_LANGUAGES.includes(stored as LanguageCode)) {
      return stored as LanguageCode;
    }
  } catch {
    // localStorage not available
  }
  return detectBrowserLanguage();
}

/**
 * Configuration options for useLanguage hook
 */
export interface UseLanguageOptions {
  /** Initial language (default: auto-detect from browser, fallback 'en') */
  defaultLanguage?: LanguageCode;
  /** i18next namespaces to load (default: ['common']) */
  namespaces?: string | string[];
}

/**
 * Legacy translation function type (deprecated)
 * @deprecated Use ti18n with namespace keys instead
 */
export type TranslationFunction = (koText: string, enText: string) => string;

/**
 * i18next translation function type
 */
export type I18nTranslationFunction = (key: string, options?: Record<string, unknown>) => string;

/**
 * Return type for useLanguage hook
 */
export interface UseLanguageReturn {
  /** Current language code */
  language: LanguageCode;
  /** Language setter function */
  setLanguage: Dispatch<SetStateAction<LanguageCode>>;
  /** Legacy translation helper - returns text based on current language @deprecated */
  t: TranslationFunction;
  /** i18next translation function - use namespace:key format */
  ti18n: I18nTranslationFunction;
  /** Whether translations are ready */
  ready: boolean;
}

/**
 * Hook for managing language state and translations
 * Auto-detects browser locale on initialization, persists choice to localStorage
 * Now integrates with react-i18next for centralized translations
 * 
 * @param options - Configuration options
 * @returns Language state and utilities
 */
export function useLanguage(options: UseLanguageOptions = {}): UseLanguageReturn {
  const { defaultLanguage, namespaces = ['common'] } = options;
  
  // Get i18next translation function and ready state
  const { t: i18nT, i18n, ready } = useTranslation(namespaces);
  
  // Use provided default, otherwise check localStorage, then auto-detect from browser
  const [language, setLanguageState] = useState<LanguageCode>(
    () => defaultLanguage ?? getInitialLanguage()
  );

  // Sync local state with i18next language
  useEffect(() => {
    const current = i18n.language;
    // Normalize i18next locale variants (e.g. 'es-MX' → 'es', 'pt' → 'pt-BR')
    const normalized: LanguageCode = current === 'ko' || current?.startsWith('ko-') ? 'ko'
      : current === 'zh-CN' || current === 'zh' || current?.startsWith('zh-') ? 'zh-CN'
      : current === 'pt-BR' || current?.startsWith('pt') ? 'pt-BR'
      : current === 'es' || current?.startsWith('es-') ? 'es'
      : current === 'de' || current?.startsWith('de-') ? 'de'
      : current === 'fr' || current?.startsWith('fr-') ? 'fr'
      : current === 'it' || current?.startsWith('it-') ? 'it'
      : current === 'nl' || current?.startsWith('nl-') ? 'nl'
      : current === 'sv' || current?.startsWith('sv-') ? 'sv'
      : current === 'pl' || current?.startsWith('pl-') ? 'pl'
      : 'en';
    if (normalized !== language) {
      setLanguageState(normalized);
    }
  }, [i18n.language, language]);

  // Persist language changes to localStorage and sync with i18next
  const setLanguage: Dispatch<SetStateAction<LanguageCode>> = useCallback((value) => {
    setLanguageState((prev) => {
      const newLang = typeof value === 'function' ? value(prev) : value;
      try {
        localStorage.setItem(LANGUAGE_STORAGE_KEY, newLang);
        // Also update i18next
        i18nChangeLanguage(newLang as SupportedLanguage);
      } catch {
        // localStorage not available
      }
      return newLang;
    });
  }, []);

  /**
   * Legacy translation helper function (deprecated)
   * Returns Korean text when language is 'ko', English otherwise.
   * For pt-BR / es / any other locale, English is the safer fallback.
   * @deprecated Use ti18n with namespace keys instead
   */
  const t: TranslationFunction = useCallback(
    (koText: string, enText: string): string => (language === 'ko' ? koText : enText),
    [language]
  );

  /**
   * i18next translation function
   * Use namespace:key format like 'common:buttons.submit'
   */
  const ti18n: I18nTranslationFunction = useCallback(
    (key: string, opts?: Record<string, unknown>): string => i18nT(key, opts as any) as string,
    [i18nT]
  );

  return {
    language,
    setLanguage,
    t,
    ti18n,
    ready,
  };
}

export default useLanguage;
